import React, { useState, useEffect } from "react";
import {
  GlobeAltIcon,
  ClockIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { getTimezoneInfo } from "../utils/gmtUtils";

const TimezoneModal = ({ isOpen, onClose }) => {
  const [timezoneInfo, setTimezoneInfo] = useState(null);
  const [currentTime, setCurrentTime] = useState(null);

  useEffect(() => {
    const updateTime = () => {
      const tzInfo = getTimezoneInfo();
      setTimezoneInfo(tzInfo);
      setCurrentTime(new Date());
    };

    updateTime();
    const interval = setInterval(updateTime, 1000); // Update every second for real-time display

    return () => clearInterval(interval);
  }, []);

  if (!isOpen || !timezoneInfo) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-50 rounded-full">
              <GlobeAltIcon className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Timezone Information</h2>
              <p className="text-sm text-gray-500">Your local timezone details</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <XMarkIcon className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
            <ClockIcon className="w-5 h-5 text-gray-500" />
            <div>
              <p className="text-sm text-gray-600">Current Time</p>
              <p className="text-lg font-semibold text-gray-900">
                {currentTime?.toLocaleTimeString()}
              </p>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex justify-between items-center py-2 border-b border-gray-100">
              <span className="text-sm text-gray-600">Timezone:</span>
              <span className="text-sm font-medium text-gray-900">
                {timezoneInfo.local.split("/").pop().replace("_", " ")}
              </span>
            </div>

            <div className="flex justify-between items-center py-2 border-b border-gray-100">
              <span className="text-sm text-gray-600">GMT Offset:</span>
              <span className="text-sm font-medium text-gray-900">
                GMT{timezoneInfo.offset}
              </span>
            </div>

            <div className="flex justify-between items-center py-2 border-b border-gray-100">
              <span className="text-sm text-gray-600">Full Timezone:</span>
              <span className="text-sm font-medium text-gray-900">
                {timezoneInfo.local}
              </span>
            </div>

            {timezoneInfo.isDST && (
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-sm text-gray-600">DST:</span>
                <span className="text-sm font-medium text-green-600">
                  Active
                </span>
              </div>
            )}

            <div className="flex justify-between items-center py-2">
              <span className="text-sm text-gray-600">Timezone ID:</span>
              <span className="text-sm font-medium text-gray-900">
                {timezoneInfo.name}
              </span>
            </div>
          </div>

          {/* Info section */}
          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <h3 className="text-sm font-medium text-blue-900 mb-2">How it works</h3>
            <p className="text-xs text-blue-800 leading-relaxed">
              All server times are stored in GMT and automatically converted to your local timezone for display. 
              This ensures consistent time handling across different timezones.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="w-full bg-gray-900 text-white py-2 px-4 rounded-lg hover:bg-gray-800 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default TimezoneModal; 